import {Box, Text, Input, Flex, Listbox, createListCollection} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {LuSearch, LuCheck} from 'react-icons/lu'

function NoElementsFoundIndicator() {
  const {t} = useTranslate('common')
  return (
    <Box px={4} py={2}>
      <Text fontSize="sm" color="gray.500">
        {t('no_results_for_applied_filters')}
      </Text>
    </Box>
  )
}

interface ListSearchInputProps {
  placeholder: string
  value: string
  onChange: (value: string) => void
}

export function ListSearchInput({placeholder, value, onChange}: ListSearchInputProps) {
  return (
    <Box p={2} borderBottom="1px solid" borderColor="gray.200">
      <Box position="relative">
        <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" pointerEvents="none" color="gray.400">
          <LuSearch size={16} />
        </Box>
        <Input
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoFocus
          size="sm"
          pl={10}
        />
      </Box>
    </Box>
  )
}

interface FilterListboxProps<T extends string, M extends boolean = false> {
  items: Array<{label: string; value: T; icon?: React.ReactNode}>
  value?: M extends true ? T[] : T
  multiple?: M
  onValueChange: M extends true ? (value: T[]) => void : (value: T) => void
  maxH?: number
}

export function FilterListbox<T extends string, M extends boolean = false>({
  items,
  value,
  multiple = false as M,
  onValueChange,
  maxH = 200,
}: FilterListboxProps<T, M>) {
  const collection = createListCollection({
    items: items,
  })

  return (
    <Listbox.Root
      collection={collection}
      value={Array.isArray(value) ? value : value ? [value] : []}
      maxH={maxH}
      selectionMode={multiple ? 'multiple' : 'single'}
      overflowY="auto"
      onValueChange={details => {
        if (multiple) {
          ;(onValueChange as (value: T[]) => void)(details.value as T[])
        } else {
          ;(onValueChange as (value: T) => void)(details.value[0] as T)
        }
      }}
    >
      <Listbox.Content border="none" p={0} gap={0}>
        {items.length > 0 ? (
          items.map(item => (
            <Listbox.Item key={item.value} item={item} borderRadius="none" px={4} py={2}>
              <Listbox.ItemText>
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={2}>
                    {item.icon && <Box color="gray.500">{item.icon}</Box>}
                    <Text fontSize="sm" textTransform="capitalize">
                      {item.label}
                    </Text>
                  </Flex>
                  <Listbox.ItemIndicator>
                    <LuCheck size={16} />
                  </Listbox.ItemIndicator>
                </Flex>
              </Listbox.ItemText>
            </Listbox.Item>
          ))
        ) : (
          <NoElementsFoundIndicator />
        )}
      </Listbox.Content>
    </Listbox.Root>
  )
}
